import { formatMoneyAmount } from '@occc-app/lib/currency';

import { DAY_MS, MAX_COMFORTABLE_TICKS_COUNT } from './metric-chart.constants';
import { MetricTimePoint } from './metric-chart.types';

const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

export const tickNumberFormatter = (value: number) => compact.format(value);

export const tickMoneyFormatter = (value: number) => `$${tickNumberFormatter(value)}`;

export const tickDateFormatter = (time: number) => {
  return new Date(time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const tickTimeFormatter = (time: number) => {
  return new Date(time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

export const tickDateTimeFormatter = (time: number) => {
  return new Date(time).toLocaleTimeString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Formats a duration expressed in minutes into an hours label like `6H` (rounded to nearest hour)
export const tickDurationHourFormatter = (minutes: number) => `${Math.round(minutes / 60)}H`;

// Formats a duration expressed in milliseconds into an hour label like `6H` (rounded)
export const tickDurationHourFromMsFormatter = (ms: number) =>
  `${Math.round(ms / (60 * 60 * 1000))}H`;

export const defaultLegendFormatter = (value: string) => {
  return value.replace(/^value\./, '');
};

export const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
}).format;

export const moneyFormatter = (value: number, centsThreshold?: number) => {
  const showCents =
    centsThreshold !== undefined ? value !== 0 && Math.abs(value) < centsThreshold : true;
  return formatMoneyAmount(value, { showCents });
};

const hasMinThreshold = (timestamps: number[], thresholdMs: number): boolean => {
  if (timestamps.length < 2) return false;
  for (let i = 1; i < timestamps.length; i++) {
    const diff = Math.abs(timestamps[i] - timestamps[i - 1]);
    if (diff < thresholdMs) return true;
  }
  return false;
};

const inWithinWindow = (timestamps: number[], windowMs: number): boolean => {
  const minDate = new Date(Math.min(...timestamps));
  const maxDate = new Date(Math.max(...timestamps));
  const minStart = new Date(minDate);
  minStart.setHours(0, 0, 0, 0);
  const maxEnd = new Date(maxDate);
  maxEnd.setHours(23, 59, 59, 999);
  return maxEnd.getTime() - minStart.getTime() <= windowMs;
};

export const getTicksSettings = (
  data: Array<{ date: number; value: MetricTimePoint['value'] }>,
  options: {
    minTicksCount?: number;
    maxTicksCount?: number;
    dateTickFormatter?: (time: number) => string;
  } = {},
) => {
  const {
    minTicksCount = 5,
    maxTicksCount = MAX_COMFORTABLE_TICKS_COUNT,
    dateTickFormatter = tickDateFormatter,
  } = options;

  let ticks: number[] = [];
  let ticksFormatter = dateTickFormatter;
  let ticksShift = 0;
  let keys = ['value'];

  const maxIndex = data.length - 1;
  const actualMinTicksCount = Math.max(2, minTicksCount);

  if (data.length) {
    const timestamps = data.map((v) => v.date);
    if (data.length > 1 && hasMinThreshold(timestamps, DAY_MS)) {
      ticksFormatter = inWithinWindow(timestamps, DAY_MS)
        ? tickTimeFormatter
        : tickDateTimeFormatter;
    }

    if (data.length <= maxTicksCount) {
      ticks = data.map((d) => d.date);
    } else {
      const step = Math.max(1, Math.floor(maxIndex / (actualMinTicksCount - 1)));
      ticks = Array.from({ length: actualMinTicksCount }, (_, i) => {
        if (i === 0) {
          return data[0].date;
        }
        if (i === actualMinTicksCount - 1) {
          return data[maxIndex].date;
        }
        return data[Math.min(i * step, maxIndex)].date;
      });
    }

    if (typeof data[0].value !== 'number') {
      keys = Object.keys(data[0].value).map((key) => `value.${key}`);
    }

    ticksShift = Math.round(Math.abs(data[maxIndex].date - data[0].date) / (maxIndex || 1) / 2);
  }

  return { ticks, ticksFormatter, ticksShift, keys };
};
